import { useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3';
import { gsap } from 'gsap';
import { useApp } from '../context/AppContext';
import { formatNaira } from '../utils/formatters';
import { getStateColor } from '../utils/colors';
import { getComparisonData } from '../utils/dataLoader';

export function ComparisonChart() {
  const { state, data, deselectState } = useApp();
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dotsRef = useRef<Map<string, SVGCircleElement>>(new Map());
  const prevYearRef = useRef<number>(state.selectedYear);
  
  // Get comparison data for selected states
  const comparisonData = useMemo(() => {
    if (!data || state.selectedStates.length === 0) return [];
    return getComparisonData(state.selectedStates, data.stateSummaries, data.years);
  }, [data, state.selectedStates]);
  
  // Create scales (memoized)
  const { xScale, yScale, innerWidth, innerHeight, margin } = useMemo(() => {
    if (!containerRef.current || comparisonData.length === 0) {
      return { xScale: null, yScale: null, innerWidth: 0, innerHeight: 0, margin: { top: 20, right: 120, bottom: 40, left: 70 } };
    }
    
    const containerWidth = containerRef.current.clientWidth;
    const height = 300;
    const m = { top: 20, right: 120, bottom: 40, left: 70 };
    const w = containerWidth - m.left - m.right;
    const h = height - m.top - m.bottom;
    
    const allData = comparisonData.flatMap((d) => d.data);
    
    const x = d3
      .scaleLinear()
      .domain(d3.extent(allData, (d) => d.year) as [number, number])
      .range([0, w]);
    
    const y = d3
      .scaleLinear()
      .domain([0, d3.max(allData, (d) => d.total) ?? 0])
      .nice()
      .range([h, 0]);
    
    return { xScale: x, yScale: y, innerWidth: w, innerHeight: h, margin: m };
  }, [comparisonData]);
  
  // D3 rendering
  useEffect(() => {
    if (!svgRef.current || !containerRef.current || comparisonData.length === 0 || !xScale || !yScale) return;
    
    const svg = d3.select(svgRef.current);
    const containerWidth = containerRef.current.clientWidth;
    const height = 300;
    
    svg.attr('width', containerWidth).attr('height', height);
    svg.selectAll('*').remove();
    dotsRef.current.clear();
    
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
    
    // Grid lines
    g.append('g')
      .attr('class', 'grid')
      .attr('opacity', 0.1)
      .call(
        d3.axisLeft(yScale)
          .tickSize(-innerWidth)
          .tickFormat(() => '')
      );
    
    // X axis
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3.axisBottom(xScale)
          .tickFormat((d) => String(d))
          .ticks(Math.min(data?.years.length ?? 10, 10))
      )
      .attr('color', '#64748b')
      .selectAll('text')
      .attr('fill', '#94a3b8')
      .attr('font-size', '11px');
    
    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(yScale)
          .tickFormat((d) => formatNaira(d as number, true))
          .ticks(5)
      )
      .attr('color', '#64748b')
      .selectAll('text')
      .attr('fill', '#94a3b8')
      .attr('font-size', '11px');
    
    // Lines for each state
    const line = d3
      .line<{ year: number; total: number }>()
      .x((d) => xScale(d.year))
      .y((d) => yScale(d.total))
      .curve(d3.curveMonotoneX);
    
    comparisonData.forEach((stateData, index) => {
      const color = getStateColor(index);
      
      // Area under line
      const area = d3
        .area<{ year: number; total: number }>()
        .x((d) => xScale(d.year))
        .y0(innerHeight)
        .y1((d) => yScale(d.total))
        .curve(d3.curveMonotoneX);
      
      g.append('path')
        .datum(stateData.data)
        .attr('d', area)
        .attr('fill', color)
        .attr('fill-opacity', 0)
        .transition()
        .delay(index * 100 + 500)
        .duration(500)
        .attr('fill-opacity', 0.1);
      
      // Line path with animated drawing
      const linePath = g.append('path')
        .datum(stateData.data)
        .attr('d', line)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 2.5)
        .attr('stroke-linecap', 'round');
      
      const pathNode = linePath.node();
      if (pathNode) {
        const pathLength = pathNode.getTotalLength();
        linePath
          .attr('stroke-dasharray', pathLength)
          .attr('stroke-dashoffset', pathLength);
        
        // Animate with elastic/bouncy effect
        gsap.to(pathNode, {
          strokeDashoffset: 0,
          duration: 1.2,
          ease: 'elastic.out(1, 0.6)',
          delay: index * 0.15,
        });
      }
      
      // Data points for current year
      const currentYearPoint = stateData.data.find(d => d.year === state.selectedYear);
      if (currentYearPoint) {
        const dot = g.append('circle')
          .attr('class', `dot-${stateData.state}`)
          .attr('cx', xScale(currentYearPoint.year))
          .attr('cy', yScale(currentYearPoint.total))
          .attr('r', 0)
          .attr('fill', color)
          .attr('stroke', '#0f172a')
          .attr('stroke-width', 2);
        
        const dotNode = dot.node();
        if (dotNode) {
          dotsRef.current.set(stateData.state, dotNode);
          
          // Bounce in the dot
          gsap.to(dotNode, {
            attr: { r: 6 },
            duration: 0.6,
            ease: 'elastic.out(1.2, 0.4)',
            delay: 0.8 + index * 0.1,
          });
        }
      }
    });
    
    // Current year indicator line
    const yearLine = g.append('line')
      .attr('class', 'year-line')
      .attr('x1', xScale(state.selectedYear))
      .attr('x2', xScale(state.selectedYear))
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', '#475569')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4 4')
      .attr('opacity', 0);
    
    gsap.to(yearLine.node(), {
      opacity: 1,
      duration: 0.5,
      delay: 0.5,
    });
    
    g.append('text')
      .attr('x', xScale(state.selectedYear))
      .attr('y', -8)
      .attr('text-anchor', 'middle')
      .attr('fill', '#94a3b8')
      .attr('font-size', '11px')
      .attr('opacity', 0)
      .text(state.selectedYear)
      .each(function() {
        gsap.to(this, { opacity: 1, duration: 0.3, delay: 0.6 });
      });
    
    // Legend
    const legend = svg
      .append('g')
      .attr('transform', `translate(${containerWidth - margin.right + 10}, ${margin.top})`);
    
    comparisonData.forEach((stateData, index) => {
      const color = getStateColor(index);
      const legendItem = legend
        .append('g')
        .attr('transform', `translate(0, ${index * 25})`)
        .style('cursor', 'pointer')
        .on('click', () => deselectState(stateData.state));
      
      legendItem
        .append('rect')
        .attr('width', 12)
        .attr('height', 12)
        .attr('rx', 2)
        .attr('fill', color)
        .attr('opacity', 0)
        .each(function() {
          gsap.to(this, { opacity: 1, duration: 0.3, delay: 0.8 + index * 0.1 });
        });
      
      legendItem
        .append('text')
        .attr('x', 18)
        .attr('y', 10)
        .attr('fill', '#e2e8f0')
        .attr('font-size', '11px')
        .attr('opacity', 0)
        .text(stateData.state)
        .each(function() {
          gsap.to(this, { opacity: 1, duration: 0.3, delay: 0.85 + index * 0.1 });
        });
      
      // Remove button
      legendItem
        .append('text')
        .attr('x', 100)
        .attr('y', 10)
        .attr('fill', '#64748b')
        .attr('font-size', '10px')
        .text('×')
        .style('cursor', 'pointer')
        .on('mouseenter', function() {
          d3.select(this).attr('fill', '#ef4444');
        })
        .on('mouseleave', function() {
          d3.select(this).attr('fill', '#64748b');
        });
    });
    
    prevYearRef.current = state.selectedYear;
  }, [comparisonData, data?.years, margin, innerWidth, innerHeight, xScale, yScale, deselectState]);
  
  // Animate dots when year changes
  useEffect(() => {
    if (!xScale || !yScale || comparisonData.length === 0) return;
    if (prevYearRef.current === state.selectedYear) return;
    
    comparisonData.forEach((stateData) => {
      const dot = dotsRef.current.get(stateData.state);
      if (!dot) return;
      
      const currentYearPoint = stateData.data.find(d => d.year === state.selectedYear);
      if (currentYearPoint) {
        // Animate dot to new position with elastic effect
        gsap.to(dot, {
          attr: {
            cx: xScale(currentYearPoint.year),
            cy: yScale(currentYearPoint.total),
          },
          duration: 0.6,
          ease: 'elastic.out(1, 0.5)',
        });
        
        // Pulse effect
        gsap.fromTo(dot,
          { attr: { r: 6 } },
          { 
            attr: { r: 8 },
            duration: 0.15,
            yoyo: true,
            repeat: 1,
            ease: 'power2.inOut',
          }
        );
      }
    });
    
    // Also animate the year indicator line
    if (svgRef.current) {
      const yearLine = d3.select(svgRef.current).select('.year-line');
      gsap.to(yearLine.node(), {
        attr: {
          x1: xScale(state.selectedYear),
          x2: xScale(state.selectedYear),
        },
        duration: 0.5,
        ease: 'elastic.out(1, 0.7)',
      });
    }
    
    prevYearRef.current = state.selectedYear;
  }, [state.selectedYear, comparisonData, xScale, yScale]);
  
  if (!state.compareMode || state.selectedStates.length === 0) {
    return null;
  }
  
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-display font-semibold text-surface-100">
          State Comparison
        </h3>
        <span className="badge-accent">
          {state.selectedStates.length} of 3 states
        </span>
      </div>
      
      <div ref={containerRef} className="w-full">
        <svg ref={svgRef} className="w-full" />
      </div>
      
      <p className="text-xs text-surface-500 mt-2 text-center">
        Click legend items to remove from comparison
      </p>
    </div>
  );
}
